import { authFetch } from '../../../api/csrf';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Download,
  Play,
  RefreshCw,
  IndianRupee,
  Users,
  Calculator,
  X,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  Loader2
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { toast } from 'react-hot-toast';
import { payrollRunApi } from '../../../api/payrollRunApi';
import { formatDate } from '../../../utils/stringUtils';
import WarningPopup from '../../../components/WarningPopup';
import ExportModal from '../../../components/common/ExportModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const formatDateValue = (value, options = { includeTime: false }) => {
  if (!value) return 'N/A';
  const formatted = formatDate(value, options);
  return formatted || 'N/A';
};

const formatPeriodDisplay = (run) => {
  if (!run) return 'N/A';

  const start = run.payPeriodStart;
  const end = run.payPeriodEnd;

  if (start || end) {
    const formattedStart = start ? formatDateValue(start, { includeTime: false }) : '';
    const formattedEnd = end ? formatDateValue(end, { includeTime: false }) : '';

    if (formattedStart && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
    if (formattedStart) return formattedStart;
    if (formattedEnd) return formattedEnd;
  }

  const period = run.period;
  if (!period) return 'N/A';

  const monthYearMatch = /^(\d{4})-(\d{2})$/;
  if (monthYearMatch.test(period)) {
    const [, year, month] = period.match(monthYearMatch);
    const parsedDate = new Date(Number(year), Number(month) - 1, 1);
    const formatted = formatDate(parsedDate, { includeTime: false });
    return formatted || period;
  }
  const formatted = formatDate(period, { includeTime: false });
  return formatted || period;
};

const formatPayGroupName = (name) => {
  if (!name) return 'N/A';
  const cleaned = name.replace(/\s*\(Default\)$/i, '').trim();
  return cleaned || name;
};

const formatCurrencyInr = (value) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const PayrollRun = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL search params
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 10);
  const [showRunModal, setShowRunModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  // New backend filters
  const [runStartDate, setRunStartDate] = useState(searchParams.get('startDate') || '');
  const [runEndDate, setRunEndDate] = useState(searchParams.get('endDate') || '');
  const [minNetPay, setMinNetPay] = useState(searchParams.get('minNetPay') || '');
  const [minNetPayInput, setMinNetPayInput] = useState(searchParams.get('minNetPay') || '');
  const [maxNetPay, setMaxNetPay] = useState(searchParams.get('maxNetPay') || '');
  const [maxNetPayInput, setMaxNetPayInput] = useState(searchParams.get('maxNetPay') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  const [runFormData, setRunFormData] = useState({
    payGroup: '',
    period: '',
    // runType: 'regular',
    includeAdjustments: false,
    includeReimbursements: false,
    includeLoans: false
  });

  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [runToDelete, setRunToDelete] = useState(null);
  const [isSubmittingRun, setIsSubmittingRun] = useState(false);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'minNetPay', 'maxNetPay', 'page', 'limit'];
      managedKeys.forEach(key => params.delete(key));

      if (searchTerm) params.set('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (runStartDate) params.set('startDate', runStartDate);
      if (runEndDate) params.set('endDate', runEndDate);
      if (minNetPay) params.set('minNetPay', minNetPay);
      if (maxNetPay) params.set('maxNetPay', maxNetPay);
      if (page && page !== 1) params.set('page', String(page));
      if (limit && limit !== 10) params.set('limit', String(limit));

      return params;
    }, { replace: true });
  }, [searchTerm, statusFilter, runStartDate, runEndDate, minNetPay, maxNetPay, page, limit, setSearchParams]);



  // Debounce effects
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMinNetPay(minNetPayInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [minNetPayInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMaxNetPay(maxNetPayInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [maxNetPayInput]);



  const payrollRunsQueryKey = [
    'payroll',
    'runs',
    searchTerm,
    statusFilter,
    runStartDate,
    runEndDate,
    minNetPay,
    maxNetPay,
    page,
    limit
  ];

  const {
    data: responseData,
    isLoading: payrollRunsLoading,
    isFetching: payrollRunsFetching
  } = useQuery({
    queryKey: payrollRunsQueryKey,
    queryFn: async () => {
      const response = await payrollRunApi.getPayrollRuns({
        search: searchTerm,
        status: statusFilter,
        runStartDate,
        runEndDate,
        minNetPay,
        maxNetPay,
        page,
        limit
      });
      if (response?.responseCode === 200) {
        return response;
      }
      toast.error(response?.responseMessage || 'Failed to load payroll runs');
      return { data: [], pagination: null };
    },
    keepPreviousData: true,
    enabled: !minNetPay || !maxNetPay || Number(maxNetPay) >= Number(minNetPay)
  });

  const baseExportFilters = useMemo(() => ({
    search: searchTerm,
    status: statusFilter,
    runStartDate,
    runEndDate,
    minNetPay,
    maxNetPay,
  }), [searchTerm, statusFilter, runStartDate, runEndDate, minNetPay, maxNetPay]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: 'payroll-runs',
        scope,
        format,
        filters,
        page: overridePage ?? page,
        limit: overrideLimit ?? limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Export failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `payroll-runs-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, limit]
  );

  const payrollRuns = responseData?.data || [];
  const pagination = responseData?.pagination || null;

  const createPayrollRunMutation = useMutation({
    mutationFn: async (payload) => {
      try {
        const response = await payrollRunApi.createPayrollRun(payload);
        return response;
      } catch (error) {
        throw error;
      }
    },
    onMutate: () => {
      setIsSubmittingRun(true);
    },
    onSuccess: (response) => {
      // onSuccess is only called when the request succeeds, so always show success
      // Extract meaningful success message from response data
      const successMessage = response?.responseMessage ||
        response?.data?.message ||
        (response?.data?.payrollRunId
          ? `Payroll run created successfully for ${response.data.payPeriod || 'the selected period'}`
          : 'Payroll run created successfully');

      toast.success(successMessage);
      setShowRunModal(false);

      queryClient.invalidateQueries(['payroll', 'runs']);
    },
    onError: (error) => {

      // Extract error message from backend response
      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        'Error creating payroll run';



      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#fee2e2',
          color: '#991b1b',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
        },
      });
    },
    onSettled: () => {
      setIsSubmittingRun(false);
    }
  });

  const deletePayrollRunMutation = useMutation({
    mutationFn: (id) => payrollRunApi.deletePayrollRun(id),
    onSuccess: (response) => {
      const responseCode = response?.responseCode ?? response?.status ?? response?.statusCode;
      const message = response?.responseMessage || response?.message || 'Payroll run deleted successfully';

      const isSuccess =
        responseCode === 200 ||
        responseCode === 204 ||
        response?.success === true ||
        message.toLowerCase().includes('success');

      if (isSuccess) {
        // toast.success(message); // Removed to avoid duplicate notifications
        queryClient.invalidateQueries(['payroll', 'runs']);
      } else {
        toast.error(message || 'Failed to delete payroll run');
      }
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        'Error deleting payroll run';
      toast.error(errorMessage);
    }
  });

  const isTableLoading = payrollRunsLoading || payrollRunsFetching;
  const isCreatingRun = isSubmittingRun || createPayrollRunMutation.isLoading;

  // Export payroll runs


  // Refresh payroll runs
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
  }, [queryClient]);

  const handleRunPayroll = () => {
    if (isCreatingRun || !validateRunForm()) {
      return;
    }
    createPayrollRunMutation.mutate(runFormData);
  };

  // Validate form
  const validateRunForm = () => {
    const errors = {};

    if (!runFormData.payGroup) {
      errors.payGroup = 'Pay group is required';
    }

    if (!runFormData.period?.trim()) {
      errors.period = 'Period is required';
    } else if (!/^\d{4}-\d{2}$/.test(runFormData.period)) {
      errors.period = 'Period must be in YYYY-MM format (e.g., 2024-01)';
    }

    setFormErrors(errors);

    const firstError = Object.values(errors)[0];
    if (firstError) {
      toast.error(firstError);
      return false;
    }

    return true;
  };

  const handleDeletePayrollRun = (run) => {
    setRunToDelete(run);
    setIsBulkDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePayrollRun = async () => {
    if (!runToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deletePayrollRunMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} payroll run(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} payroll run(s) could not be deleted.`);
        }
      } else {
        await deletePayrollRunMutation.mutateAsync(runToDelete.id || runToDelete._id);
        toast.success('Payroll run deleted successfully.');
      }
      setShowDeleteConfirm(false);
      setRunToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete payroll run. Please try again.');
      }
    }
  };

  const cancelDeletePayrollRun = () => {
    setShowDeleteConfirm(false);
    setRunToDelete(null);
    setIsBulkDelete(false);
  };

  const sortedRuns = payrollRuns;

  // Frontend sorting removed as we are moving to backend logic where possible
  // and user task specifically asked for removing frontend logic

  // Reset selected rows when filters or search change
  useEffect(() => {
    setSelectedRows(new Set());
    setPage(1);
  }, [
    searchTerm,
    statusFilter,
    runStartDate,
    runEndDate,
    minNetPay,
    maxNetPay
  ]);

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (e) => {
    const currentPageIds = sortedRuns.map(run => run.id || run._id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows);
    if (!ids.length) {
      toast.error('No selected runs found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} run(s) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {
      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && !sortedRuns.length) {
          toast.error('No data on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, runStartDate: startDate, runEndDate: endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [sortedRuns, baseExportFilters, requestBackendExport]
  );

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Optimized form handlers to prevent cursor jumping
  const handleRunFormChange = useCallback((field, value) => {
    let processedValue = value;

    // Period field is a month input (YYYY-MM format), so preserve the value as-is
    if (field === 'period') {
      // Month input type returns YYYY-MM format, so we don't need to filter it
      processedValue = value;
    } else {
      // Text fields: only allow alphanumeric, spaces, and commas
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (processedValue.length > 50) processedValue = processedValue.slice(0, 50);
    }

    setRunFormData(prev => ({
      ...prev,
      [field]: processedValue || value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);


  const handleCloseRunModal = useCallback(() => {
    setShowRunModal(false);
    setFormErrors({});
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Run</h1>
          <p className="text-gray-600 mt-1">Execute payroll runs and manage processing</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="payroll" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="payroll" action="read">
            <div className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? 'rotate-180' : ''}`} />
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                onClose={() => setShowInlineSelectedFormats(false)}
                onExport={handleExportSelected}
                loading={exportLoading}
                className="absolute right-0 mt-2 z-50 min-w-[200px]"
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="primary"
              icon={<Play className="h-4 w-4" />}
              onClick={() => {
                // Open with current values; no hardcoded defaults
                setShowRunModal(true);
              }}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
            >
              Run Payroll
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-grow min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Pay Group..."
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchInput(value);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setSearchTerm('');
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1 ml-1">Run Date Range</label>
            <DateRangeFilter
              startDate={runStartDate}
              endDate={runEndDate}
              onChange={({ startDate, endDate }) => {
                setRunStartDate(startDate);
                setRunEndDate(endDate);
              }}
              inputClassName="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 uppercase mb-1 ml-1">Net Pay Range</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Min"
                value={minNetPayInput}
                min="0"
                max="100000000"
                onChange={(e) => {
                  let val = e.target.value;
                  if (val > 100000000) val = 100000000;
                  setMinNetPayInput(val);
                }}
                className={`w-24 px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green ${minNetPayInput && maxNetPayInput && Number(maxNetPayInput) < Number(minNetPayInput) ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                placeholder="Max"
                value={maxNetPayInput}
                min="0"
                max="100000000"
                onChange={(e) => {
                  let val = e.target.value;
                  if (val > 100000000) val = 100000000;
                  setMaxNetPayInput(val);
                }}
                className={`w-24 px-2 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green ${minNetPayInput && maxNetPayInput && Number(maxNetPayInput) < Number(minNetPayInput) ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
            </div>
            {minNetPayInput && maxNetPayInput && Number(maxNetPayInput) < Number(minNetPayInput) && (
              <p className="text-[10px] text-red-500 mt-1 absolute">Max must be ≥ Min</p>
            )}
          </div>

          {(searchInput || statusFilter !== 'all' || runStartDate || runEndDate || minNetPayInput || maxNetPayInput) && (
            <div className="flex-shrink-0 ml-auto">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  setSearchInput('');
                  setSearchTerm('');
                  setStatusFilter('all');
                  setRunStartDate('');
                  setRunEndDate('');
                  setMinNetPayInput('');
                  setMinNetPay('');
                  setMaxNetPayInput('');
                  setMaxNetPay('');
                }}
              >
                Clear All
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Payroll Runs List */}
      {isTableLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green"></div>
        </div>
      ) : sortedRuns.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-16 text-center">
          <p className="text-gray-500 text-sm">No payroll runs found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                      checked={sortedRuns.length > 0 && selectedRows.size === sortedRuns.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Pay Group</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Period</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Headcount</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Net Pay</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Run Date</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRuns.map((run) => (
                  <tr key={run.id} className={`hover:bg-gray-50 ${selectedRows.has(run.id || run._id) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                        checked={selectedRows.has(run.id || run._id)}
                        onChange={() => handleSelectRow(run.id || run._id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-agrosaf-green to-agrosaf-blue flex items-center justify-center">
                            <Calculator className="h-4 w-4 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{formatPayGroupName(run.payGroup)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPeriodDisplay(run)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {run.headcount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrencyInr(run.netPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateValue(run.runDate, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleDeletePayrollRun(run)}
                          className="text-red-600 hover:text-red-800"
                          disabled={deletePayrollRunMutation.isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && (
            <div className="border-t border-gray-200">
              <PaginationControls
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                limit={pagination.limit}
                hasNextPage={pagination.hasNextPage}
                hasPrevPage={pagination.hasPrevPage}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Run Payroll Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Run Payroll</h2>
                <button
                  onClick={handleCloseRunModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pay Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={runFormData.payGroup}
                    onChange={(e) => handleRunFormChange('payGroup', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formErrors.payGroup
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-agrosaf-green focus:border-transparent'
                      }`}
                  >
                    <option value="">Select Pay Group</option>
                    <option value="monthly">Monthly Payroll</option>
                  </select>
                  {formErrors.payGroup && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.payGroup}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Period <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="month"
                    key={runFormData.period}  // This forces re-mount when value changes
                    value={runFormData.period || ""}
                    onChange={(e) => handleRunFormChange("period", e.target.value)}
                    onClick={(e) => e.target.showPicker()}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formErrors.period
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-agrosaf-green focus:border-transparent'
                      }`}
                  />
                  {formErrors.period && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.period}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={handleCloseRunModal}
                >
                  Cancel
                </Button>
                <PermissionGate permission="payroll" action="create">
                  <Button
                    variant="primary"
                    icon={
                      isCreatingRun ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )
                    }
                    onClick={handleRunPayroll}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                    disabled={isCreatingRun}
                  >
                    {isCreatingRun ? 'Processing...' : 'Run Payroll'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      <WarningPopup
        isOpen={showDeleteConfirm}
        onClose={cancelDeletePayrollRun}
        onConfirm={confirmDeletePayrollRun}
        title={isBulkDelete ? "Delete Payroll Runs" : "Delete Payroll Run"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected payroll run(s)? This action cannot be undone.`
            : (runToDelete
              ? `Are you sure you want to delete the ${formatPayGroupName(runToDelete.payGroup || '')} payroll for ${formatPeriodDisplay(runToDelete)}? This action cannot be undone.`
              : "Are you sure you want to delete this payroll run?")
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Payroll Runs"
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />
    </div>
  );
};

export default PayrollRun;